import { computed, inject, Injectable } from '@angular/core';

import { WcgToastService } from '@wcg/frontend-framework';

import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { delay, pipe, switchMap, tap } from 'rxjs';

import {
  PickUpRequestParams,
  PickUpYouResponse,
  PICK_UP_SIGNAL_STATE,
} from '../../types';
import { DashboardService } from '../../services';
import { PICK_UP_DEFAULT_SIGNAL_STATE } from '@data-access/dash-board/constants';
import { tapResponse } from '@ngrx/operators';

export const PICK_SIGNAL_STATE = signalState<PICK_UP_SIGNAL_STATE>({
  ...PICK_UP_DEFAULT_SIGNAL_STATE,
});

@Injectable()
export class PickupSignalState {
  private readonly _dashboardService = inject(DashboardService);
  private readonly _wcgToastService = inject(WcgToastService);
  private readonly _pickUpSignalState = PICK_SIGNAL_STATE;

  readonly $data = computed<PickUpYouResponse | null>(() =>
    this._pickUpSignalState.data(),
  );
  readonly $isLoading = computed(() => this._pickUpSignalState.isLoading());
  readonly $error = computed(() => this._pickUpSignalState.error());

  /**
   * Loads the "Pick up where you left off" items into state.
   *
   * @param requestParams - Filter/paging parameters for the request.
   * @param useMock - Forwarded to `DashboardService.getPickUpWhereyouLeft`;
   * when true the service serves its typed mock payload instead of calling
   * the API. Driven by the card component's `mockToggle` input.
   */
  getPickUpWhereYouLeftStates = rxMethod<{
    requestParams: PickUpRequestParams;
    useMock?: boolean;
  }>(
    pipe(
      tap(() =>
        patchState(this._pickUpSignalState, { isLoading: true, error: null }),
      ),
      switchMap(({ requestParams, useMock }) =>
        this._dashboardService
          .getPickUpWhereyouLeft(requestParams, useMock === true)
          .pipe(
            delay(250),
            tapResponse({
              error: (error: Error) =>
                patchState(this._pickUpSignalState, {
                  error: error,
                  isLoading: false,
                }),
              next: (response) => {
                patchState(this._pickUpSignalState, {
                  error: null,
                  isLoading: false,
                  data: response,
                });
              },
            }),
          ),
      ),
    ),
  );

  /**
   * @NOTE: PUBLIC METHODS
   */
  resetState(): void {
    patchState(this._pickUpSignalState, {
      ...PICK_UP_DEFAULT_SIGNAL_STATE,
    });
  }

  /**
   * Raises a dismissible toast for a non-blocking, informational message.
   *
   * Used by the "Waiting on you" card when a user action cannot be completed —
   * e.g. a row that carries no navigable identifiers.
   *
   * @param message - Display copy shown to the user.
   * @example
   * this._pickUpSignalState.displayToast('Can not navigate to Cardio Phase II');
   */
  displayToast(message: string): void {
    this._wcgToastService.open({
      data: { message },
      duration: 5000,
    });
  }

  loadErrorState() {
    patchState(this._pickUpSignalState, {
      isLoading: false,
      error: new Error('Error State Loading'),
    });
  }

  loadEmptyState() {
    patchState(this._pickUpSignalState, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState() {
    patchState(this._pickUpSignalState, {
      isLoading: true,
      error: null,
      data: null,
    });
  }
}
